<template>
   <div class="fc-mobile-nav">
      <ul class="fc-mobile-nav__list">
         <li v-for="(item, index) in visibleItems" :key="index" class="fc-mobile-nav__item">
            <button
               v-if="item.externalProperties && item.externalProperties.dropdown"
               type="button"
               class="fc-mobile-nav__link"
               :aria-expanded="openIndex === index"
               @click="toggle(index)"
            >
               {{ item.header }}
               <i
                  class="fc_dropdown fc-mobile-nav__chev"
                  :class="{ 'is-open': openIndex === index }"
               ></i>
            </button>
            <a
               v-else
               class="fc-mobile-nav__link"
               itemprop="url"
               :href="item.link"
               @click.prevent="goTo(item.link)"
            >
               {{ item.header }}
            </a>

            <div
               v-if="item.externalProperties && item.externalProperties.dropdown"
               v-show="openIndex === index"
               class="fc-mobile-nav__panel"
            >
               <!-- flat dropdown (Products) -->
               <ul
                  v-if="item.externalProperties.sidebysideDropdown"
                  class="fc-mobile-nav__group-items"
               >
                  <li v-for="(link, lIndex) in flatItems(item.items)" :key="lIndex">
                     <a
                        class="fc-mobile-nav__group-link"
                        itemprop="url"
                        :href="link.link"
                        @click.prevent="goTo(link.link)"
                     >
                        <span v-if="hasIcons(flatItems(item.items))" class="fc-mobile-nav__icon">
                           <span v-if="resolveLinkIcon(link)" v-html="resolveLinkIcon(link)"></span>
                           <i v-else :class="link.icon"></i>
                        </span>
                        <span class="fc-mobile-nav__label"
                           >{{ link.header
                           }}<span
                              v-if="link.externalProperties && link.externalProperties.isPopular"
                              class="fc-mega-menu__badge fc-mega-menu__badge--popular"
                              >Popular</span
                           ></span
                        >
                     </a>
                  </li>
               </ul>

               <!-- grouped dropdown (Developers, Resources) -->
               <div
                  v-if="
                     item.externalProperties.stylishDropdown ||
                        item.externalProperties.simpleDropdown
                  "
               >
                  <div
                     v-for="(group, gIndex) in item.items"
                     :key="gIndex"
                     class="fc-mobile-nav__group"
                  >
                     <h3 class="fc-mobile-nav__eyebrow">{{ group.header }}</h3>
                     <ul class="fc-mobile-nav__group-items">
                        <li v-for="(link, lIndex) in frameworkItems(group.items)" :key="lIndex">
                           <a
                              v-if="link.header"
                              class="fc-mobile-nav__group-link"
                              itemprop="url"
                              :href="link.link"
                              @click.prevent="goTo(link.link)"
                           >
                              <span v-if="hasIcons(group.items)" class="fc-mobile-nav__icon">
                                 <span
                                    v-if="resolveLinkIcon(link)"
                                    v-html="resolveLinkIcon(link)"
                                 ></span>
                                 <i v-else :class="link.icon"></i>
                              </span>
                              <span>{{ link.header }}</span>
                           </a>
                           <ul v-if="link.items && link.items.length" class="fc-mobile-nav__nested">
                              <li v-for="(sub, sIndex) in frameworkItems(link.items)" :key="sIndex">
                                 <a
                                    class="fc-mobile-nav__group-link"
                                    itemprop="url"
                                    :href="sub.link"
                                    @click.prevent="goTo(sub.link)"
                                 >
                                    <span v-if="sub.icon" class="fc-mobile-nav__icon">
                                       <span
                                          v-if="resolveLinkIcon(sub)"
                                          v-html="resolveLinkIcon(sub)"
                                       ></span>
                                       <i v-else :class="sub.icon"></i>
                                    </span>
                                    <span
                                       >{{ sub.header
                                       }}<span
                                          v-if="isUpdated(sub)"
                                          class="fc-mega-menu__badge fc-mega-menu__badge--updated"
                                          >Updated</span
                                       ></span
                                    >
                                 </a>
                              </li>
                           </ul>
                           <a
                              v-if="moreLink(link.items)"
                              class="fc-mobile-nav__more"
                              itemprop="url"
                              :href="moreLink(link.items).link"
                              @click.prevent="goTo(moreLink(link.items).link)"
                           >
                              {{ moreLink(link.items).header }}
                           </a>
                        </li>
                     </ul>
                  </div>

                  <!-- Customer quote / case-study callout - Resources menu
                       only, same content as SimpleDropdown.vue's desktop
                       version (kept in sync manually - see its own
                       TODO(content) note about there being no Contentful
                       field for this yet). Not the same __quote class/CSS,
                       though: that one is position:absolute + a fixed
                       252px width, meant to sit beside the columns in a
                       flex row - here it's just another full-width block
                       in the mobile panel's normal column flow. -->
                  <div v-if="item.header === 'Resources'" class="fc-mobile-nav__quote">
                     <span class="fc-mobile-nav__quote-mark" aria-hidden="true">&rdquo;</span>
                     <blockquote class="fc-mobile-nav__quote-text">
                        There are very few software tools or products I have worked with over the
                        years which have been as powerful and easy to use as FusionCharts.
                     </blockquote>
                     <div class="fc-mobile-nav__quote-footer">
                        <figcaption class="fc-mobile-nav__quote-author">
                           Mike McColl, FooTelePros
                        </figcaption>
                        <a
                           class="fc-mobile-nav__quote-cta"
                           itemprop="url"
                           href="/customers"
                           @click.prevent="goTo('/customers')"
                           >Read the story &rarr;</a
                        >
                     </div>
                  </div>
               </div>
            </div>
         </li>
      </ul>

      <div class="fc-mobile-nav__actions">
         <!-- Same split as the desktop nav's CTA loop (PrimaryNavigation.vue):
              "Buy Now" keeps a plain-text-link look, other CTAs (e.g. "Try
              FusionCharts") get the solid pill. Replaces the old hardcoded
              "Talk to us" link (not CMS-driven, duplicated the "Buy Now" CTA
              this loop already renders) and the single-item ctaItem pill. -->
         <a
            v-for="(item, index) in ctaItems"
            :key="index"
            :href="item.link"
            :class="
               item.header === 'Buy Now'
                  ? 'fc-mobile-nav__talk'
                  : 'fc-pill-btn fc-pill-btn--solid fc-pill-btn--sm'
            "
            itemprop="url"
            @click.prevent="goToDownload(item.link)"
         >
            {{ item.header }}
         </a>
      </div>
   </div>
</template>

<script>
import { resolveLinkIcon } from '../../util/navIcons';

export default {
   name: 'MobileNav',
   props: {
      items: {
         type: Array,
         default: () => [],
      },
   },
   data() {
      return {
         openIndex: null,
      };
   },
   computed: {
      visibleItems() {
         return this.items.filter(
            item =>
               (item.externalProperties && item.externalProperties.dropdown) ||
               (item.type === 'navigation' && item.link)
         );
      },
      ctaItems() {
         return this.items.filter(item => item.type === 'cta' && item.link !== '/contact-sales');
      },
   },
   methods: {
      resolveLinkIcon,
      toggle(index) {
         this.openIndex = this.openIndex === index ? null : index;
      },
      hasIcons(items) {
         return (items || []).some(link => link.icon);
      },
      flatItems(items) {
         return (items || []).filter(
            item => !(item.externalProperties && item.externalProperties.comparision)
         );
      },
      frameworkItems(items) {
         return (items || []).filter(item => item.type !== 'cta');
      },
      isUpdated(item) {
         const props = item.externalProperties;
         return !!(props && (props.IsUpdate || props.isUpdate || props.isupdate));
      },
      moreLink(items) {
         return (items || []).find(item => item.type === 'cta') || null;
      },
      goTo(url) {
         const reload = this.$reloadType(url);
         if (reload) {
            this.$openReload(url);
         } else {
            this.$router.push(url).catch(() => {});
         }
         this.$emit('navigate');
      },
      goToDownload(link) {
         if (this.$reloadType(link)) {
            this.$openReload(link);
            this.$emit('navigate');
            return;
         }
         const fw = this.$route.query.framework || this.$getUserSelectedFramework();
         const url = fw ? `${link}?framework=${fw}` : link;
         this.$router.push(url).catch(() => {});
         this.$emit('navigate');
      },
   },
};
</script>

<style lang="scss" scoped>
.fc-mobile-nav {
   border-top: 1px solid #e6e8ef;
   padding: 4px 0 18px;
   // No max-height/overflow-y here anymore - PrimaryNavigation.vue's
   // .mobile-menu__panel (this element's fixed-position parent) is now the
   // single scroll owner for the whole menu. Nesting a second independent
   // overflow:auto region inside it was ambiguous for touch scroll
   // ownership and is exactly what wasn't scrolling on mobile.

   &__list {
      list-style-type: none;
      margin: 0;
      padding: 0;
   }
   &__item + &__item {
      border-top: 1px solid #f0f1f5;
   }
   &__link {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      padding: 13px 16px;
      background: none;
      border: none;
      text-align: left;
      text-decoration: none;
      font-family: basefontSemiBold, 'Helvetica Neue', Arial, sans-serif;
      font-size: 16px;
      font-weight: 700;
      color: #0b0b28;
      cursor: pointer;

      &:visited,
      &:hover,
      &:visited:hover {
         text-decoration: none;
      }
   }
   &__chev {
      font-size: 8px;
      opacity: 0.55;
      transition: transform 0.18s ease;

      &.is-open {
         transform: rotate(180deg);
      }
   }
   &__panel {
      padding: 4px 16px 16px;
   }
   &__group + &__group {
      margin-top: 22px;
   }
   &__eyebrow {
      margin: 0 0 10px;
      font-size: 11px;
      font-family: basefontSemiBold, 'Helvetica Neue', Arial, sans-serif;
      font-weight: 700;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: #8b90a8;
   }
   &__group-items {
      list-style-type: none;
      margin: 0;
      padding: 0;
   }
   &__group-link {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 9px 6px;
      border-radius: 10px;
      text-decoration: none;
      color: #212344;
      font-size: 14.5px;
      font-family: basefontSemiBold, 'Helvetica Neue', Arial, sans-serif;
      font-weight: 700;

      &:visited,
      &:hover,
      &:visited:hover {
         text-decoration: none;
      }
      &:active {
         background: #f5f6fb;
      }
   }
   &__label {
      // The "New"/"Popular" badge (app.scss's shared __badge rule) is
      // position:absolute, meant to perch above this label specifically
      // (matching .fc-mega-menu__label's own position:relative on
      // desktop) - without this, it escapes up to whatever ancestor
      // *is* positioned instead (the mobile panel itself), landing near
      // the top of the whole menu rather than next to its own label.
      position: relative;
   }
   &__icon {
      flex: 0 0 auto;
      width: 30px;
      height: 30px;
      border-radius: 8px;
      background: #f2eaff;
      color: #5b2fb0;
      display: grid;
      place-items: center;

      i {
         font-size: 15px;
      }
      // The v-html'd icon markup wraps the <svg> in its own inline <span>
      // (see the template) - an inline box sizes to the inherited
      // line-height (1.65, set globally), not to the 16px SVG it actually
      // contains, so place-items:center above was centering that taller
      // phantom box instead of the visible icon, pushing every icon a few
      // px above true center. display:flex collapses the span to its
      // content's real size.
      ::v-deep span {
         display: flex;
      }
      ::v-deep svg {
         width: 16px;
         height: 16px;
      }
   }
   &__nested {
      list-style-type: none;
      // Flush with the top-level group items above (Quick Start etc.), not
      // indented - these sub-items carry their own icon column just like
      // those do, so there's nothing distinguishing "nested under a
      // parent" left to visually indent for once that column exists.
      margin: 2px 0;
      padding: 0;
   }
   &__more {
      display: inline-flex;
      margin: 4px 0 4px 6px;
      font-size: 11.5px;
      font-weight: 600;
      color: #73748c;
      text-decoration: none;

      &:hover,
      &:visited,
      &:visited:hover {
         text-decoration: none;
         color: #5b2fb0;
      }
   }
   // Same colors/marks as the desktop quote panel (app.scss's
   // .fc-mega-menu__quote), but as a plain full-width block instead of a
   // fixed-width flex sibling - there's no columns row to sit beside here.
   &__quote {
      margin: 14px 0 4px;
      padding: 18px;
      background: #ded9fb;
      border: 1px solid #cec6f7;
      border-radius: 14px;

      &-mark {
         display: block;
         font-family: Georgia, 'Times New Roman', serif;
         font-size: 30px;
         font-weight: 700;
         line-height: 0.5;
         color: #2a2360;
      }
      &-text {
         margin: 12px 0 0;
         font-style: italic;
         font-size: 14px;
         line-height: 1.5;
         color: #1d1750;
      }
      &-footer {
         display: flex;
         align-items: baseline;
         justify-content: space-between;
         gap: 12px;
         margin-top: 14px;
      }
      &-author {
         margin: 0;
         font-style: italic;
         font-size: 11px;
         color: #4b4489;
      }
      &-cta {
         flex: 0 0 auto;
         font-size: 13px;
         font-weight: 700;
         color: #2a2360;
         text-decoration: none;
         white-space: nowrap;

         &:hover,
         &:visited,
         &:visited:hover {
            color: #2a2360;
            text-decoration: underline;
         }
      }
   }
   &__actions {
      display: flex;
      gap: 10px;
      padding: 14px 16px 0;
      border-top: 1px solid #e6e8ef;
      margin-top: 6px;

      .fc-pill-btn {
         flex: 1;
      }
      .fc-pill-btn--solid {
         text-transform: uppercase;
         font-family: basefontSemiBold, 'Helvetica Neue', Arial, sans-serif;
      }
   }
   // "Buy Now" in the actions row below - plain text link, matching the
   // desktop nav's .nav-talk-to-us (PrimaryNavigation.vue), not an outline
   // pill like the old hardcoded "Talk to us" link this class used to be.
   &__talk {
      flex: 1;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      color: #3c415c;
      padding: 10px 18px;
      font-size: 15px;
      text-decoration: none;

      &:visited,
      &:hover,
      &:visited:hover {
         text-decoration: none;
         color: #3c415c;
      }
   }
}
</style>
